import { GradientBorder, IconBox, VisionCard } from "@/components/VisionUI"
import { Button } from "@/components/ui/button"
import { Field } from "@/components/ui/field"
import { RouterLink } from "@/components/ui/router-link"
import { useSemanticColors } from "@/hooks/useSemanticColors"
import { effects } from "@/theme"
import { Container, Flex, Heading, Input, Text, VStack } from "@chakra-ui/react"
import { createFileRoute, redirect } from "@tanstack/react-router"
import { type SubmitHandler, useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { FaEnvelope } from "react-icons/fa"
import useAuth, { isLoggedIn } from "../hooks/useAuth"
import { emailPattern } from "../utils"

interface FormData {
  email: string
}

export const Route = createFileRoute("/recover-password")({
  component: RecoverPassword,
  beforeLoad: async () => {
    if (isLoggedIn()) {
      throw redirect({
        to: "/",
      })
    }
  },
})

function RecoverPassword() {
  const { t } = useTranslation("auth")
  const { recoverPassword } = useAuth()
  const sc = useSemanticColors()
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>()

  const onSubmit: SubmitHandler<FormData> = async (data) => {
    await recoverPassword.mutateAsync(data)
  }

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={sc.pageBg}
      backgroundImage={effects.gradients.bgGradient}
      backgroundAttachment="fixed"
      p={{ base: 3, md: 4 }}
    >
      <Container maxW="md" px={0}>
        <GradientBorder borderRadius="30px" p={0}>
          <VisionCard
            variant="elevated"
            p={{ base: 5, md: 8 }}
            borderRadius="30px"
          >
            <VStack gap={6} as="form" onSubmit={handleSubmit(onSubmit)}>
              <Flex direction="column" align="center" gap={4}>
                <IconBox variant="gradient" size="xl">
                  <FaEnvelope fontSize="36px" />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("recover.title")}
                </Heading>
                <Text color={sc.textMuted} textAlign="center">
                  {t("recover.subtitle")}
                </Text>
              </Flex>

              <VStack w="100%" gap={4}>
                <Field
                  required
                  invalid={!!errors.email}
                  errorText={errors.email?.message}
                >
                  <Input
                    {...register("email", {
                      required: "Email is required",
                      pattern: emailPattern,
                    })}
                    placeholder={t("fields.email")}
                    type="email"
                    size="lg"
                    bg="bg.glass"
                    borderColor="border.default"
                    _hover={{
                      borderColor: "border.muted",
                    }}
                    _focus={{
                      borderColor: "brand.200",
                      boxShadow: "0 0 0 1px var(--vision-colors-brand-200)",
                    }}
                    required
                  />
                </Field>

                <Button
                  type="submit"
                  loading={isSubmitting}
                  size="lg"
                  w="100%"
                  bg="brand.200"
                  color="white"
                  _hover={{
                    bg: "brand.300",
                    transform: "translateY(-2px)",
                    boxShadow: "0px 8px 24px rgba(52, 161, 221, 0.5)",
                  }}
                >
                  {t("recover.submit")}
                </Button>

                <Text color={sc.textMuted} fontSize="sm">
                  {t("recover.remember")}{" "}
                  <RouterLink
                    to="/login"
                    color="brand.200"
                    fontWeight="semibold"
                  >
                    {t("signup.logIn")}
                  </RouterLink>
                </Text>
              </VStack>
            </VStack>
          </VisionCard>
        </GradientBorder>
      </Container>
    </Flex>
  )
}
